import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useDomain } from "../hooks/useDomain";
import { api } from "../lib/api";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  domains: string[];
  customDomain?: string | null;
  status: string;
  defaultLocale: string;
  settings?: Record<string, any>;
  trialStartDate?: string | Date | null;
  trialEndDate?: string | Date | null;
  billingPlanId?: string | null;
  subscriptionStartDate?: string | Date | null;
  subscriptionEndDate?: string | Date | null;
  logoUrl?: string | null;
  authImages?: string[];
}

interface TenantContextType {
  tenant: Tenant | null;
  isLoading: boolean;
  isMainPlatform: boolean;
  tenantNotFound: boolean;
  setTenant: (tenant: Tenant | null) => void;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tenantNotFound, setTenantNotFound] = useState(false);
  const domainContext = useDomain();

  useEffect(() => {
    if (domainContext.isMainPlatform) {
      // Main platform - no tenant context
      setIsLoading(false);
      setTenantNotFound(false);
      return;
    }

    // Load tenant from domain
    loadTenantFromDomain();
  }, [domainContext]);

  const loadTenantFromDomain = async () => {
    try {
      let publicTenantInfo: any = null;

      // Validate tenant exists for this domain (public endpoint)
      try {
        // Pass hostname so API resolves tenant when nginx proxies with Host: api.philolms.com
        const validationResponse = await api.get("/tenants/public/validate", {
          params: { domain: window.location.hostname },
        });
        if (!validationResponse.data.exists) {
          setTenantNotFound(true);
          setIsLoading(false);
          return;
        }
        publicTenantInfo = validationResponse.data.tenant;
      } catch (validationError: any) {
        const status = validationError.response?.status;
        const exists = validationError.response?.data?.exists;
        if (status === 404 || status === 500 || exists === false || !exists) {
          console.warn("Tenant validation failed:", {
            status,
            exists,
            domain: window.location.hostname,
            error: validationError.message,
          });
          setTenantNotFound(true);
          setIsLoading(false);
          return;
        }
        console.error("Unexpected error during tenant validation:", validationError);
      }

      // Skip authenticated tenant fetch on Paystack return — cold load can 401 /tenants/me before
      // AuthProvider finishes and axios would redirect to /login. Public branding is enough here.
      const onPaymentCallback = window.location.pathname.startsWith("/payment/callback");

      const setTenantFromPublic = () => {
        if (!publicTenantInfo) return;
        const brandingSettings: Record<string, any> = {};
        if (publicTenantInfo.primaryColor) {
          brandingSettings.branding = { primaryColor: publicTenantInfo.primaryColor };
        }
        setTenant({
          id: publicTenantInfo.id,
          name: publicTenantInfo.name,
          slug: publicTenantInfo.slug,
          domains: [],
          status: publicTenantInfo.status,
          defaultLocale: "en",
          logoUrl: publicTenantInfo.logoUrl || null,
          authImages: publicTenantInfo.authImages || [],
          settings: Object.keys(brandingSettings).length > 0 ? brandingSettings : undefined,
        });
      };

      try {
        if (!onPaymentCallback) {
          const response = await api.get("/tenants/me");
          setTenant(response.data);
        } else {
          setTenantFromPublic();
        }
      } catch {
        // Not authenticated — use public info as fallback (login/register pages)
        setTenantFromPublic();
      }
      setIsLoading(false);
    } catch (_error) {
      console.error("Failed to load tenant:", _error);
      setIsLoading(false);
    }
  };

  const refreshTenant = useCallback(async () => {
    try {
      const response = await api.get("/tenants/me");
      setTenant(response.data);
    } catch {
      // Silently fail -- keep existing tenant data
    }
  }, []);

  useEffect(() => {
    const primaryColor = (tenant?.settings as Record<string, any>)?.branding?.primaryColor;
    if (!primaryColor) return;

    const hex = primaryColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h = 0,
      s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }

    const hsl = `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
    document.documentElement.style.setProperty("--primary", hsl);
    document.documentElement.style.setProperty("--ring", hsl);

    const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    const foreground = luminance > 0.35 ? "222 47% 11%" : "210 40% 98%";
    document.documentElement.style.setProperty("--primary-foreground", foreground);
  }, [tenant?.settings]);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        isLoading,
        isMainPlatform: domainContext.isMainPlatform,
        tenantNotFound,
        setTenant,
        refreshTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};
